import React, { useEffect, useState } from 'react';
import { useImmer } from 'use-immer';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { getFasitProblemDetails } from '../../../../api-service/fasit-problem-details';
import { ICompanyLookup } from '../../../../components/lookups/company-lookup/interfaces';
import { ConfirmDialog } from '../../../../components/confirm-dialog';
import { ActivationKeyDialog } from '../../../../components/activation-key-dialog';
import { useMetaTitle } from '../../../../hooks';
import { usePostToTab } from '../../../search/hooks/usePostToTab';
import { RegistrationCounterComponent, RegistrationCounterErrors, RegistrationCounterState } from './registration-counter-component';
import { useAttendanceRights } from '../../use-attendance-rights';

const client = moduleClientFactory.createAttendanceClient();

export const REFRESH_REGISTRATION_COUNTER_TABLE = 'REFRESH_REGISTRATION_COUNTER_TABLE';

export function RegistrationCounter() {
  const { registrationCounterId } = useParams<{ registrationCounterId?: string }>();
  const [registrationCounter, setRegistrationCounter] = useImmer<RegistrationCounterState>({ isActivated: false, hasActivationKey: false });
  const [errors, setErrors] = useImmer<RegistrationCounterErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isGeneratingActivationKey, setIsGeneratingActivationKey] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isActivationKeyWarningOpen, setIsActivationKeyWarningOpen] = useState(false);
  const [activationKey, setActivationKey] = useState<string>();
  const { enqueueSnackbar } = useSnackbar();
  const postUpdate = usePostToTab(REFRESH_REGISTRATION_COUNTER_TABLE);
  const { canEditRegistrationCounters } = useAttendanceRights();

  useMetaTitle(registrationCounterId ? 'Redigér infostander' : 'Opret infostander');

  async function loadRegistrationCounter(id: string) {
    const result = await client.getRegistrationCounterById({ registrationCounterId: id });
    if (result.registrationCounter) {
      const { name, companyId, externalReference, isActivated, hasActivationKey } = result.registrationCounter;
      setRegistrationCounter({ name, companyId, externalReference, isActivated, hasActivationKey });
    }
  }

  useEffect(() => {
    if (!registrationCounterId) {
      return;
    }
    loadRegistrationCounter(registrationCounterId).catch(() => enqueueSnackbar('Der skete en fejl ved hentning af infostander', { variant: 'error' }));
  }, [registrationCounterId]);

  function handleValidate() {
    const validationErrors: RegistrationCounterErrors = {};
    if (!registrationCounter.name) {
      validationErrors.name = 'Navn skal angives';
    }
    if (!registrationCounter.companyId) {
      validationErrors.companyId = 'Virksomhed skal angives';
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  }

  async function handleSaveClick() {
    const { name, companyId, externalReference } = registrationCounter;
    if (!name || !companyId) {
      return;
    }
    try {
      setIsSaving(true);
      if (registrationCounterId) {
        await client.updateRegistrationCounter({ registrationCounter: { registrationCounterId, companyId, name, externalReference } });
      } else {
        await client.createRegistrationCounter({ registrationCounter: { companyId, name, externalReference } });
      }
      postUpdate(window.opener);
      window.close();
    } catch (error) {
      const fasitError = getFasitProblemDetails(error);
      if (fasitError?.status === 400 && fasitError.title?.includes('eksterne reference')) {
        setErrors(draft => {
          draft.externalReference = fasitError.title;
        });
      } else {
        enqueueSnackbar('Der skete en fejl ved gemning af infostander', { variant: 'error' });
      }
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    if (!registrationCounterId) {
      return;
    }
    try {
      setIsDeleting(true);
      await client.deleteRegistrationCounter({ registrationCounterId });
      setIsDeleteDialogOpen(false);
      postUpdate(window.opener);
      window.close();
    } catch (error) {
      enqueueSnackbar('Der skete en fejl ved sletning af infostander', { variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  }

  function handleGenerateActivationKeyClick() {
    if (registrationCounter.isActivated || registrationCounter.hasActivationKey) {
      setIsActivationKeyWarningOpen(true);
      return;
    }
    generateActivationKey();
  }

  async function generateActivationKey() {
    if (!registrationCounterId) {
      return;
    }
    setIsActivationKeyWarningOpen(false);
    try {
      setIsGeneratingActivationKey(true);
      const result = await client.generateActivationKey({ registrationCounterId });
      setActivationKey(result.activationKey);
      await loadRegistrationCounter(registrationCounterId);
      postUpdate(window.opener);
    } catch (error) {
      enqueueSnackbar('Der skete en fejl ved generering af aktiveringsnøgle', { variant: 'error' });
    } finally {
      setIsGeneratingActivationKey(false);
    }
  }

  function handleNameChange(name: string) {
    setRegistrationCounter(draft => {
      draft.name = name;
    });
    setErrors(draft => {
      draft.name = undefined;
    });
  }

  function handleExternalReferenceChange(externalReference: string) {
    setRegistrationCounter(draft => {
      draft.externalReference = externalReference;
    });
    setErrors(draft => {
      draft.externalReference = undefined;
    });
  }

  function handleCompanyChange(company?: ICompanyLookup) {
    setRegistrationCounter(draft => {
      draft.companyId = company?.id;
    });
    setErrors(draft => {
      draft.companyId = undefined;
    });
  }

  return (
    <>
      <RegistrationCounterComponent
        isEditMode={!!registrationCounterId}
        isReadOnly={!canEditRegistrationCounters}
        isSaving={isSaving}
        isDeleting={isDeleting}
        isGeneratingActivationKey={isGeneratingActivationKey}
        registrationCounter={registrationCounter}
        errors={errors}
        onSaveClick={handleSaveClick}
        onValidate={handleValidate}
        onDeleteClick={() => setIsDeleteDialogOpen(true)}
        onGenerateActivationKeyClick={handleGenerateActivationKeyClick}
        onNameChange={handleNameChange}
        onExternalReferenceChange={handleExternalReferenceChange}
        onCompanyChange={handleCompanyChange}
      />
      {isDeleteDialogOpen && (
        <ConfirmDialog
          open
          onConfirmClick={handleDelete}
          title="Vil du slette denne infostander?"
          confirmButtonText="Ja, slet"
          onCancelClick={() => setIsDeleteDialogOpen(false)}
          cancelButtonText="Afbryd"
          type="warning"
        />
      )}
      {isActivationKeyWarningOpen && (
        <ConfirmDialog
          open
          onConfirmClick={generateActivationKey}
          title="Generér ny aktiveringsnøgle?"
          confirmButtonText="Generér ny nøgle"
          onCancelClick={() => setIsActivationKeyWarningOpen(false)}
          cancelButtonText="Afbryd"
          type="warning"
          message={`Infostander '${registrationCounter.name}' har allerede en aktiveringsnøgle eller en aktiveret enhed. Når du genererer en ny nøgle, ugyldiggøres den eksisterende nøgle, og en eventuel aktiveret enheds adgang tilbagekaldes.`}
        />
      )}
      {activationKey && <ActivationKeyDialog activationKey={activationKey} onConfirmClick={() => setActivationKey(undefined)} />}
    </>
  );
}
